import { useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { SortingState } from '@cast/design-system';

import { apiClient } from '@cast/infrastructure-api';

const SECURITY_WORKLOADS_QUERY_KEY = 'security-workloads';

export type SecurityInsightsWorkloadsQueryFilters = {
  clusterIds?: string[];
  namespaces?: string[];
  labels?: string[];
  search?: string;
};

type UseSecurityInsightsWorkloadsQueryParams = {
  filters: SecurityInsightsWorkloadsQueryFilters;
  sortingState?: SortingState;
  enabled?: boolean;
  pageLimit?: number;
};

const getSecurityInsightsWorkloadsQueryKey = ({
  filters,
  sortingState,
}: Pick<UseSecurityInsightsWorkloadsQueryParams, 'filters' | 'sortingState'>) =>
  [SECURITY_WORKLOADS_QUERY_KEY, filters, sortingState] as const;

export const useSecurityInsightsWorkloadsQuery = ({
  filters,
  sortingState,
  enabled = true,
  pageLimit = 50,
}: UseSecurityInsightsWorkloadsQueryParams) => {
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteQuery({
    queryKey: getSecurityInsightsWorkloadsQueryKey({ filters, sortingState }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.securityInsights.getWorkloads({
        clusterIds: filters.clusterIds,
        namespaces: filters.namespaces,
        labels: filters.labels,
        search: filters.search,
        pageLimit: String(pageLimit),
        pageCursor: pageParam,
        sortField: sortingState?.columnId,
        sortOrder: sortingState?.sortBy.direction,
      });

      return data;
    },
    enabled,
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor || undefined;
    },
  });

  return {
    isLoading,
    data: flatMap(data?.pages, (page) => page?.resources ?? []),
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    count: data?.pages[0].count ?? 0,
    isFetching,
  };
};
